// DCF 페이지 — 바닐라 dcf.html/dcf.js 이식. 프로젝트 데이터는 바닐라와
// localStorage/서버 DB 를 그대로 공유한다 (stores/dcf.ts 참고).
//
// 섹션별 URL (바닐라의 hash 방식을 라우트로 승격 — 새로고침·공유 보존).
//   /dcf            홈(히어로)          /dcf/projects   프로젝트 목록
//   /dcf/new        새 프로젝트         /dcf/p/:id#탭   워크스페이스 딥링크
import { useEffect, useState } from "react";
import { Navigate, Route, Routes, useNavigate, useParams } from "react-router-dom";
import ContactFooter from "../components/ContactFooter";
import DcfDashboard from "../components/dcf/DcfDashboard";
import DcfHome from "../components/dcf/DcfHome";
import DcfNewProject from "../components/dcf/DcfNewProject";
import DcfWorkspace from "../components/dcf/DcfWorkspace";
import { LANGS, useI18n, useT } from "../lib/i18n";
import { useAuth } from "../stores/auth";
import { useDcf } from "../stores/dcf";
import "../styles/dcf.css";
import { BrandMark } from "../ui/BrandMark";

/** /dcf/p/:projectId — 프로젝트 딥링크. init 완료 후 열고, 없으면 목록으로. */
function WorkspaceRoute() {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const current = useDcf((s) => s.current);
  const projects = useDcf((s) => s.projects);
  const openProject = useDcf((s) => s.openProject);

  useEffect(() => {
    if (!projectId) return;
    if (current?.project.id === projectId) return;
    if (projects.some((p) => p.id === projectId)) {
      openProject(projectId);
    } else if (projects.length) {
      // 목록 로드 후에도 없음 — 삭제됐거나 다른 계정의 링크
      navigate("/dcf/projects", { replace: true });
    }
    // projects 가 아직 비어 있으면(서버 동기화 전) 다음 렌더에서 재시도된다
  }, [projectId, current, projects, openProject, navigate]);

  if (!current || current.project.id !== projectId) {
    return <div className="page active" />;
  }
  return (
    <div className="page active">
      <DcfWorkspace onBack={() => navigate("/dcf/projects")} />
    </div>
  );
}

// 채팅과 같은 테마 계약 — data-theme 속성 + va_theme 저장 키 (ChatPage.cycleTheme 참조)
const THEME_ORDER = ["light", "ivory", "dark"] as const;
const THEME_LABEL: Record<string, string> = { light: "라이트", ivory: "아이보리", dark: "다크" };

export default function DcfPage() {
  const { user, logout } = useAuth();
  const init = useDcf((s) => s.init);
  const busy = useDcf((s) => s.busy);
  const { lang, t } = useT();
  const setLang = useI18n((s) => s.setLang);
  const navigate = useNavigate();
  const path = location.pathname;
  const [theme, setTheme] = useState(
    () => document.documentElement.getAttribute("data-theme") || "light",
  );

  const cycleTheme = () => {
    const cur = document.documentElement.getAttribute("data-theme") || "light";
    const next =
      THEME_ORDER[(THEME_ORDER.indexOf(cur as (typeof THEME_ORDER)[number]) + 1) %
        THEME_ORDER.length];
    if (next === "light") document.documentElement.removeAttribute("data-theme");
    else document.documentElement.setAttribute("data-theme", next);
    try {
      localStorage.setItem("va_theme", next);
      localStorage.setItem("va_theme_v2", "1");
    } catch {
      /* noop */
    }
    setTheme(next);
  };

  // 로그인 사용자 기준 프로젝트 로드 (로컬 캐시 즉시 + 서버 병합)
  useEffect(() => {
    init(user);
  }, [user, init]);

  const goWorkspace = () => {
    const cur = useDcf.getState().current;
    if (cur) navigate(`/dcf/p/${cur.project.id}`);
  };

  return (
    <div className="dcf-root">
      <nav className="nav">
        <a
          className="nav-logo"
          href="/dcf"
          onClick={(e) => {
            e.preventDefault();
            navigate("/dcf");
          }}
        >
          <BrandMark className="logo-mark" />
          <strong>Deepsearch</strong>
          <span className="tag">DCF 워크스페이스</span>
        </a>
        <button className="nav-link nav-back" onClick={() => (location.href = "/")}>
          {t("nav.back")}
        </button>
        <div className="nav-links">
          <button
            className={`nav-link${path.endsWith("/dcf") ? " active" : ""}`}
            onClick={() => navigate("/dcf")}
          >
            {t("nav.home")}
          </button>
          <button
            className={`nav-link${path.includes("/dcf/projects") ? " active" : ""}`}
            onClick={() => navigate("/dcf/projects")}
          >
            {t("nav.projects")}
          </button>
        </div>
        {/* 좁은 화면에선 짧은 쪽만 보인다 — 한 줄에 다 들어가야 다른 항목이
            눌려 세로로 깨지지 않는다 (dcf.css 720px 블록) */}
        <button className="nav-btn" onClick={() => navigate("/dcf/new")}>
          <span className="nav-btn-long">+ New Valuation</span>
          <span className="nav-btn-short">+ New</span>
        </button>
        <button
          className="nav-logout"
          title={`테마: ${THEME_LABEL[theme] || "라이트"} (클릭하여 전환)`}
          aria-label="테마 전환"
          onClick={cycleTheme}
        >
          {theme === "dark" ? (
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M12 3a6 6 0 0 0 9 9 9 9 0 1 1-9-9Z" />
            </svg>
          ) : (
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <circle cx="12" cy="12" r="4" />
              <path d="M12 2v2" /><path d="M12 20v2" /><path d="m4.93 4.93 1.41 1.41" />
              <path d="m17.66 17.66 1.41 1.41" /><path d="M2 12h2" /><path d="M20 12h2" />
              <path d="m6.34 17.66-1.41 1.41" /><path d="m19.07 4.93-1.41 1.41" />
            </svg>
          )}
        </button>
        <div className="lang-switch">
          {LANGS.map((l) => (
            <button
              key={l}
              className={`lang-btn${lang === l ? " active" : ""}`}
              onClick={() => setLang(l)}
            >
              {l.toUpperCase()}
            </button>
          ))}
        </div>
        <span className="nav-user">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M19 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2" />
            <circle cx="12" cy="7" r="4" />
          </svg>
          <span>{String(user?.name || user?.username || t("nav.user_fallback"))}</span>
        </span>
        <button className="nav-logout" title={t("nav.logout")} onClick={() => void logout()}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
            <path d="m16 17 5-5-5-5" />
            <path d="M21 12H9" />
          </svg>
        </button>
      </nav>

      <Routes>
        <Route
          index
          element={
            <div className="page active">
              <DcfHome onNew={() => navigate("/dcf/new")} onProjects={() => navigate("/dcf/projects")} />
            </div>
          }
        />
        <Route
          path="projects"
          element={
            <div className="page active">
              <DcfDashboard onNew={() => navigate("/dcf/new")} onOpen={goWorkspace} />
            </div>
          }
        />
        <Route
          path="new"
          element={
            <div className="page active">
              <DcfNewProject onCancel={() => navigate("/dcf/projects")} onCreated={goWorkspace} />
            </div>
          }
        />
        <Route path="p/:projectId" element={<WorkspaceRoute />} />
        <Route path="*" element={<Navigate to="/dcf" replace />} />
      </Routes>
      <ContactFooter />

      {busy && (
        <div className="loading-overlay active">
          <div className="loading-card">
            <div className="loading-spinner" />
            <p>{busy}</p>
          </div>
        </div>
      )}
    </div>
  );
}
